import { useCallback, useEffect, useRef, useState } from "react";
import { EmailSettingsPanel } from "./EmailSettingsPanel";
import { EMAIL_IDENTITY, EMAIL_PANEL_STATES, emailPanelState } from "../emailChange.js";
import { emailFieldState } from "../emailAddress.js";

// THE EMAIL-CHANGE DOOR'S SHELL — her clause 3, *"fix their email in account settings."*
//
// **App.tsx COSTS ZERO LINES**, which is P3's own argument one preference over: it mounts inside
// `AccountMenuPanel`, where account controls already live, and it fetches its own facts rather than
// having them threaded down through a shell that is one line under its ceiling.
type Facts = { known: boolean; email: string; identity: string; rosterHeld: boolean; undeliverable: boolean };

const CLOSED: Facts = { known: false, email: "", identity: EMAIL_IDENTITY.UNKNOWN, rosterHeld: true, undeliverable: false };

export function EmailSettings({
  undeliverable = false,
  onContact = () => {},
}: {
  undeliverable?: boolean;
  onContact?: () => void;
}) {
  // **EVERY FIELD FAILS CLOSED UNTIL HER ROW ANSWERS.** `rosterHeld: true` and
  // `identity: UNKNOWN` mean an unread account draws the `unknown` face — never an editable field
  // over an account this house has not identified yet.
  const [facts, setFacts] = useState<Facts>(CLOSED);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const alive = useRef(true);
  // D-5's ruling: a `useState` guard is read from the render that BOUND the handler, so two taps
  // inside one frame both send. On this control a double fire is two identity writes racing.
  const sending = useRef(false);
  // R216's law, one surface over: only the NEWEST read may paint. This panel re-reads after every
  // save, so a slow first read landing behind a fresh one would paint her OLD address over the one
  // she just saved — and she would read it as the save having failed.
  const readSeq = useRef(0);
  useEffect(() => () => { alive.current = false; }, []);

  const read = useCallback(async () => {
    const ticket = readSeq.current + 1;
    readSeq.current = ticket;
    try {
      const res = await fetch("/api/member/email", { credentials: "include" });
      const d = await res.json().catch(() => null);
      if (!alive.current || readSeq.current !== ticket) return;
      // Presence, never truthiness, on every field (R53/R136/R137).
      if (d?.known === true && typeof d.email === "string") {
        setFacts({
          known: true,
          email: d.email,
          identity: d.identity === EMAIL_IDENTITY.GOOGLE || d.identity === EMAIL_IDENTITY.ADDRESS ? d.identity : EMAIL_IDENTITY.UNKNOWN,
          rosterHeld: d.rosterHeld !== false,
          undeliverable: d.undeliverable === true,
        });
        // The field is SEEDED from her own row, so a member repairing one character is repairing
        // rather than retyping from memory — and it is seeded only while she has typed nothing, or
        // a re-read after a refusal would wipe what she was in the middle of correcting.
        setValue((typed) => (typed ? typed : d.email));
      }
    } catch { /* left unknown: an unread row is not an account with no address */ }
  }, []);

  useEffect(() => { void read(); }, [read]);

  const save = async () => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    setNote("");
    try {
      const res = await fetch("/api/member/email", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value }),
      });
      // R39/R47: this door answers HTTP 200 with `{ ok: false }` on a refusal, so a client reading
      // the status code calls a refusal a success. Read the payload.
      const data = await res.json().catch(() => null);
      setNote(String(data?.message || "That did not go through just now. Try again in a moment."));
    } catch {
      setNote("That did not go through just now. Try again in a moment.");
    } finally {
      // The re-read is what puts her new address on screen, and it is the same read that proves the
      // write landed — this panel never paints what it hoped for (`PushSettings`' own ruling).
      await read();
      sending.current = false;
      if (alive.current) setBusy(false);
    }
  };

  const state = emailPanelState(facts) || EMAIL_PANEL_STATES.UNKNOWN;
  // The live hint under the field is the SIGNUP field's own law, so a member correcting an address
  // here is told what she would have been told there.
  const field = emailFieldState(value);

  return (
    <EmailSettingsPanel
      state={state}
      email={facts.email}
      value={value}
      hint={field.hint}
      suggestion={field.suggestion}
      note={note}
      // **THE FRESHER FACT WINS, AND THE WALK IS WHAT FOUND THAT IT HAD TO (R18).** The prop is
      // what the PAGE knew at load, off the membership payload App.tsx fetches once; `facts` is what
      // her ROW says now. Before this, a member who had just repaired her address went on reading
      // *we cannot reach you* over a corrected field until she reloaded.
      undeliverable={facts.known ? facts.undeliverable : undeliverable === true}
      busy={busy}
      onValue={(next) => { setValue(next); setNote(""); }}
      onSave={() => { void save(); }}
      onContact={onContact}
    />
  );
}
